import type { SuperStats } from '@/hooks/useSuperStats';

// ─────────────────────────────────────────────────────────────
// Export CSV du centre de vue globale (KPI, classements, géo, sources).
// Séparateur « ; » + BOM UTF-8 pour une ouverture directe dans Excel FR.
// ─────────────────────────────────────────────────────────────

export interface ExportMeta {
  scopeLabel: string;
  startDate: string;
  endDate: string;
  scopeId: number;
}

function csvCell(value: string | number | null | undefined): string {
  const text = value == null ? '' : String(value);
  if (/[";\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function row(cells: (string | number | null | undefined)[]): string {
  return cells.map(csvCell).join(';');
}

function num(value: number): string {
  return value.toLocaleString('fr-FR');
}

function pct(value: number | null): string {
  if (value == null) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

export function buildSuperStatsCsv(stats: SuperStats, meta: ExportMeta): string {
  const lines: string[] = [];

  lines.push(row(['Zifek — Statistiques & vue globale']));
  lines.push(row(['Périmètre', meta.scopeLabel]));
  lines.push(row(['Période', `${meta.startDate} → ${meta.endDate}`]));
  lines.push(row(['Généré le', new Date().toLocaleString('fr-FR')]));
  lines.push('');

  lines.push(row(['KPI', 'Valeur', 'Variation vs période précédente']));
  lines.push(row(['Visites totales (depuis création)', num(stats.visitsAllTime), '']));
  lines.push(row(['Visiteurs uniques (depuis création)', num(stats.uniqueAllTime), '']));
  lines.push(row(['Visites sur la période', num(stats.visitsPeriod), pct(stats.deltas.visits)]));
  lines.push(row(['Visiteurs uniques (période)', num(stats.uniquePeriod), pct(stats.deltas.unique)]));
  lines.push(row(['Visites accueil Zifek', num(stats.landingVisits), '']));
  lines.push(row(['Visites sites commerçants', num(stats.siteVisits), '']));
  lines.push(row(['Visites pages produits', num(stats.productVisits), '']));
  lines.push(row(['Visites pages services', num(stats.serviceVisits), '']));
  lines.push(row(['Commandes (période)', num(stats.orders), pct(stats.deltas.orders)]));
  lines.push(row([`CA commandes (période) — ${stats.currency}`, num(stats.revenue), pct(stats.deltas.revenue)]));
  lines.push(row(['Comptes utilisateurs des sites', num(stats.siteUsers), '']));
  lines.push(row(['Sites / commerces', num(stats.sites), '']));
  lines.push(row(['Produits', num(stats.products), '']));
  lines.push(row(['Services', num(stats.services), '']));
  lines.push('');

  lines.push(row(['Évolution des visites']));
  lines.push(row(['Date', 'Visites']));
  stats.visitsByDay.forEach((point) => lines.push(row([point.date, point.total])));
  lines.push('');

  lines.push(row(['Sources de trafic']));
  lines.push(row(['Source', 'Visites']));
  stats.sources.forEach((source) => lines.push(row([source.name, source.value])));
  lines.push('');

  lines.push(row(['Sites les plus visités']));
  lines.push(row(['Rang', 'Site', 'Visites', 'Visiteurs uniques']));
  stats.topSites.forEach((item, index) => lines.push(row([index + 1, item.title, item.value, item.sub])));
  lines.push('');

  lines.push(row(['Produits les plus visités']));
  lines.push(row(['Rang', 'Produit', 'Visites', 'Visiteurs uniques']));
  stats.topProducts.forEach((item, index) => lines.push(row([index + 1, item.title, item.value, item.sub])));
  lines.push('');

  lines.push(row(['Services les plus visités']));
  lines.push(row(['Rang', 'Service', 'Visites', 'Visiteurs uniques']));
  stats.topServices.forEach((item, index) => lines.push(row([index + 1, item.title, item.value, item.sub])));
  lines.push('');

  lines.push(row(['Répartition géographique — Pays']));
  lines.push(row(['Pays', 'Code', 'Visites']));
  stats.geoCountries.forEach((item) => lines.push(row([item.name, item.code || '', item.value])));
  lines.push('');

  lines.push(row(['Répartition géographique — Villes']));
  lines.push(row(['Ville', 'Visites']));
  stats.geoCities.forEach((item) => lines.push(row([item.name, item.value])));

  return lines.join('\n');
}

export function downloadSuperStatsCsv(stats: SuperStats, meta: ExportMeta): void {
  const csv = buildSuperStatsCsv(stats, meta);
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `zifek-stats_${meta.startDate}_${meta.endDate}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}